import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import {Observable, firstValueFrom, lastValueFrom} from 'rxjs';
import { environment } from '../../environments/environment';
import jwt_decode from 'jwt-decode';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class PublicApiService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient, private authService: AuthService) {}

  // Fetch user fleets
  getUserFleets(): Observable<any> {
    return this.http.get(`${this.apiUrl}/fleet`);
  }

  async getFleetNumberOfLocations(fleetId: string, searchTerm: string): Promise<number> {

    const params = new HttpParams()
      .set('page', "10000")
      // .set('skip', ((page - 1) * pageSize).toString())
      // .set('sort', sort)
      .set('id', 'true');

      try {
        const url = (searchTerm ==='') ? `${this.apiUrl}/fleet/${fleetId}/location` : `${this.apiUrl}/fleet/${fleetId}/location/search/${searchTerm}`;
        const response = await lastValueFrom(this.http.get(url, {params}));

        if (response) {
         const respArray = response as any[];
         console.log(`Fleet ${fleetId} has ${respArray.length} locations`);
         return respArray.length;
        } else {
          return 0;
        }

      } catch (error) {
        console.error('Error fetching data:', error);
        throw error;
      }
  }

  // Fetch locations for a specific fleet
  getFleetLocations(fleetId: string, page: number = 1, pageSize: number = 10, sort: string = '_id', filters: string = '', sorters: string = ''): Observable<any> {
    // console.log(`Making API request to get locations for fleet: ${fleetId}`);
    const params = new HttpParams()
      .set('page', pageSize.toString())
      .set('skip', ((page - 1) * pageSize).toString())
      .set('sort', sort)
      .set('id', 'true')
      .set('filters', filters)
      .set('sorters', sorters);

    // console.log('Request parameters:', params.toString());
    return this.http.get(`${this.apiUrl}/fleet/${fleetId}/location`, { params });
  }

  getFleetLocationsWithTotal(fleetId: string, page: number = 1, pageSize: number = 10, sort: string = '_id', filters: string = '', sorters: string = ''): Observable<any> {
    // console.log(`Making API request to get locations for fleet: ${fleetId}`);
    const params = new HttpParams()
      .set('page', pageSize.toString())
      .set('skip', ((page - 1) * pageSize).toString())
      .set('sort', sort)
      .set('id', 'true')
      .set('filters', filters)
      .set('sorters', sorters);

    // console.log('Request parameters:', params.toString());
    return this.http.get(`${this.apiUrl}/fleet/${fleetId}/location-with-total`, { params });
  }

  // Fetch alerts summary for a specific fleet
  getFleetAlertsSummary(fleetId: string): Observable<any> {
    console.log(`Making API request to get alerts summary for fleet: ${fleetId}`);
    return this.http.get(`${this.apiUrl}/fleet/${fleetId}/alerts-summary`);
  }

  // Search locations in a specific fleet
  searchFleetLocations(fleetId: string, searchTerm: string, page: number = 1, pageSize: number = 10, sort: string = 'label', sortDirection: string ="asc"): Observable<any> {
    // console.log(`Making API request to search locations for fleet: ${fleetId} with search term: ${searchTerm}`);
    const params = new HttpParams()
      .set('page', pageSize.toString())
      .set('skip', ((page - 1) * pageSize).toString())
      .set('sort', sort)
      .set('sortDirection', sortDirection)
      .set('id', 'true');

    return this.http.get(`${this.apiUrl}/fleet/${fleetId}/location/search/${searchTerm}`, { params });
  }

  getNonLocationAlerts(filters: string = '', sorters: string = '', page: number = 1, pageSize: number = 10): Observable<any> {
    // console.log(`Making API request to search locations for fleet: ${fleetId} with search term: ${searchTerm}`);
    const params = new HttpParams()
      .set('filters', filters)
      .set('sorters', sorters)
      .set('page', pageSize.toString())
      .set('skip', ((page - 1) * pageSize).toString());

    return this.http.get(`${this.apiUrl}/non-location-alerts`, { params });
  }

  getFleetLocationAlerts(fleetId: string, filters: string = '', sorters: string = '', page: number = 1, pageSize: number = 10): Observable<any> {
    // console.log(`Making API request to search locations for fleet: ${fleetId} with search term: ${searchTerm}`);
    const params = new HttpParams()
      .set('filters', filters)
      .set('sorters', sorters)
      .set('page', pageSize.toString())
      .set('skip', ((page - 1) * pageSize).toString());

    return this.http.get(`${this.apiUrl}/fleet/${fleetId}/location-alerts`, { params });
  }

  // Get location query segments
  getLocationQuerySegments(locationId: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/locations/${locationId}/query-segments`);
  }

  getLocationTimeSeries(locationId: string,interval:string,querySegmentRequestsAll:any,range_start:string,range_end:string): Observable<any> {
    const params = new HttpParams()
      .set('interval', interval)
      .set('interpolation', 'none')
      .set('range_start', range_start)
      .set('range_end', range_end)
      .set('include_query', false)
      .set('query', JSON.stringify(querySegmentRequestsAll));
    return this.http.get(`${this.apiUrl}/historical/location/${locationId}/timeseries`,{ params });
  }

  // Get location details
  getLocationDetails(locationId: string): Observable<any> {
    // console.log(`Making API request to get location details for UUID: ${locationId}`);
    return this.http.get(`${this.apiUrl}/location/${locationId}/all`);
  }

  createLocationMutingRules(locationId: string, signatures: string[], from: string, to: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/location/${locationId}/muting-rules`, {
      signatures,
      from,
      to,
    });
  }

  deleteLocationMutingRules(locationId: string, ruleIds: string[]): Observable<any> {
    return this.http.delete(`${this.apiUrl}/location/${locationId}/muting-rules`, {
      body: { ruleIds },
    });
  }

  // Get location SFR details
  async getLocationSFRDetails(locationId: string): Promise<any> {
    // console.log(`Making API request to get location SFR details for UUID: ${locationId}`);
    return await lastValueFrom(this.http.get(`${this.apiUrl}/location/${locationId}/sfr`));
  }

  // Get location Projet Import details
  async getLocationProjectRecordDetails(locationId: string): Promise<any> {
    // console.log(`Making API request to get location Project Record details for UUID: ${locationId}`);
    return await lastValueFrom(this.http.get(`${this.apiUrl}/location/${locationId}/project-record`));
  }

  // Get location SFR details
  async getLocationDevicesDetails(locationId: string): Promise<any> {
    // console.log(`Making API request to get location Devices details for UUID: ${locationId}`);
    return await lastValueFrom(this.http.get(`${this.apiUrl}/location/${locationId}/devices`));
  }

  async getLocationQcData(locationId: string): Promise<any> {
    return await lastValueFrom(this.http.get(`${this.apiUrl}/location/${locationId}/qc-data`));
  }

  async updateLocationQcScanConfirmation(locationId: string, scope: string, qcId: string, qcScanConfirmed: boolean): Promise<any> {
    return await lastValueFrom(this.http.patch(`${this.apiUrl}/location/${locationId}/qc-data/${scope}/${qcId}`, {
      qc_scan_confirmed: qcScanConfirmed,
      alerts_deleted: false,
    }));
  }

  // Get user profile
  getUserProfile(): Observable<any> {
    return this.http.get(`${this.apiUrl}/user_details`);
  }

  async checkLocationExists(locationLabel: string) : Promise<boolean> {

    let response = null;

    try {

      response = await lastValueFrom(this.http.get(`${this.apiUrl}/location/exists/${encodeURIComponent(locationLabel)}`));

    } catch (error) {
      console.error('Error fetching data:', error);
      throw error;
    }

    if (response) {
      const resp = response as any;
      return resp.exists;
    } else {
      throw new Error('Invalid response from API');
    }
  }

  // Create a new location
  async createLocation(locationData: any): Promise<any> {

    try {

      const response = await lastValueFrom(this.http.post(`${this.apiUrl}/location`, locationData));

      if (response) {
        return response;
      } else {
        return null;
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      throw error;
    }
  }

  async searchHubs (hubId: string) {

    try {

      const response = await lastValueFrom(this.http.get(`${this.apiUrl}/hub/search/${encodeURIComponent(hubId)}`));

      if (response) {
        return response;
      } else {
        return null;
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      throw error;
    }

  }

  async searchFleetHubIdsForLocations (fleet: any, term: any, page: any, skip: any) {

    try {

      const response = await lastValueFrom(this.http.get(`${this.apiUrl}/fleet/${fleet}/hub/location/search/${term}?page=${page}&skip=${skip}`));

      if (response) {
        return response;
      } else {
        return null;
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      throw error;
    }
  }

  async updateHubLocation (hubId: string, locationId: string) {

    try {

      const body = { location_uuid: locationId };

      const response = await lastValueFrom(this.http.patch(`${this.apiUrl}/hub/location/${hubId}`, body));

      if (response) {
        return response;
      } else {
        return null;
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      throw error;
    }
  }

  async updateLocationFleet(locationUUID: string, fleetId: string) {
    try {

      const response = await lastValueFrom(this.http.get(`${this.apiUrl}/location/${locationUUID}/fleet/${fleetId}`));

      if (response) {
        return response;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error updating location fleet:', error);
      throw error;
    }
  }


  async addExistingUserToLocation(locationUUID: string, emailAddress: string): Promise<any> {
    try {

      const response = await lastValueFrom(this.http.get(`${this.apiUrl}/location/${locationUUID}/assign/${emailAddress}/email`));

      if (response) {
        return response;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error adding user to location:', error);
      throw error;
    }
  }

  async checkIfUserExists(emailAddress: string): Promise<any> {
    try {

      const response = await lastValueFrom(this.http.get(`${this.apiUrl}/user_details/${emailAddress}/email`));

      return !!response;

    } catch (error:any) {
      console.error('Error updating location fleet:', error);
      if(error.status === 404) {
        return false;
      }
      throw error;
    }
  }

  async createClaim (recipientEmailAddress:string,locationUUID:string){

    // Get token from the active auth0 user session
    const token = await lastValueFrom(this.authService.getAccessToken());
    if (!token) {
      throw new Error('No access token available');
    }

    const { sub } = this.decodeJwtToken(token);

    const body = {
      recipient: recipientEmailAddress,
      object: locationUUID,
      object_type: "location",
      installer: sub
    };

    try {

      const response = await lastValueFrom(this.http.post(`${this.apiUrl}/claim`, body));

      if (response) {
        return response;
      } else {
        return null;
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      throw error;
    }

  }

  /**
   * Decodes a JWT token and returns the decoded payload
   * @param token The JWT token to decode
   * @returns The decoded token payload as an object
   */
  decodeJwtToken(token: string): any {
    try {
      return jwt_decode(token);
    } catch (error) {
      console.error('Error decoding JWT token:', error);
      return null;
    }
  }

}
